import * as React from 'react';
import { Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useExportCsv, type ExportDomain } from '@/hooks/use-export-csv';
import { toast } from '@/hooks/use-toast';

interface ExportCsvButtonProps {
  domain: ExportDomain;
  query?: Record<string, string | undefined>;
  /** Label override; defaults to "Export CSV". */
  label?: string;
}

/**
 * Reusable button that triggers a streaming CSV download.
 *
 * The export hook handles the authenticated fetch + blob download dance,
 * so we just need to surface loading + error states here.
 */
export function ExportCsvButton({ domain, query, label = 'Export CSV' }: ExportCsvButtonProps) {
  const exportMut = useExportCsv();

  function handleClick(): void {
    exportMut.mutate(
      { domain, query },
      {
        onError: (err) => toast({
          variant: 'destructive',
          title: 'Export failed',
          description: err instanceof Error ? err.message : 'Try again',
        }),
      },
    );
  }

  return (
    <Button variant="outline" onClick={handleClick} disabled={exportMut.isPending}>
      {exportMut.isPending ? <Loader2 className="animate-spin" /> : <Download />}
      <span>{label}</span>
    </Button>
  );
}
